import React, { useEffect, useState } from 'react';
import Link from 'components/common/Link';
import { Box, List, ListItem, ListItemText, Typography, Divider } from '@mui/material';
import Loader from 'components/common/Loader';
import { sendApiRequest } from 'lib/api/public';
import notify from 'lib/notify';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { DashboardBlock, EmptyState, StatCard, GetHighlightStyle, listItemSx, renderListItemDate } from './index';
import { Interview } from 'components/candidate/types';
import { candidateInterviewsHref } from 'lib/detailPageTabs';

const useInterviewsWithoutNotes = ({
  getHighlightStyle,
  scrollToSection,
}: {
  getHighlightStyle: (sectionId: string) => GetHighlightStyle;
  scrollToSection: (sectionId: string) => void;
}) => {
  const [interviewsWithoutNotes, setInterviewsWithoutNotes] = useState<Interview[]>([]);
  const [totalInterviewsWithoutNotesCount, setTotalInterviewsWithoutNotesCount] = useState<number>(0);
  const [interviewsWithoutNotesCountLoading, setInterviewsWithoutNotesCountLoading] = useState<boolean>(false);

  const getInterviewsWithoutNotes = async () => {
    try {
      setInterviewsWithoutNotesCountLoading(true);

      const response = (await sendApiRequest({
        path: '/users/dashboard/interviews-without-notes',
        method: 'GET',
      })) as {
        interviewsWithoutNotes: Interview[];
        totalInterviewsWithoutNotesCount: number;
      };

      setInterviewsWithoutNotes(response.interviewsWithoutNotes);
      setTotalInterviewsWithoutNotesCount(response.totalInterviewsWithoutNotesCount);
    } catch (error) {
      notify(error);
    } finally {
      setInterviewsWithoutNotesCountLoading(false);
    }
  };

  useEffect(() => {
    void getInterviewsWithoutNotes();
  }, []);

  let mainContent = null;

  if (interviewsWithoutNotesCountLoading) {
    mainContent = <Loader />;
  } else {
    if (interviewsWithoutNotes.length > 0) {
      mainContent = (
        <>
          <List disablePadding dense>
            {interviewsWithoutNotes.map((interview, index) => (
              <React.Fragment key={interview._id}>
                {index > 0 && <Divider sx={{ backgroundColor: 'divider' }} />}
                <ListItem key={index} sx={{ ...listItemSx }}>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={500}>
                        <Link
                          href={candidateInterviewsHref(interview.candidateId || '')}
                          color="text.primary"
                          underline="hover"
                          target="_blank"
                        >
                          {interview.candidate?.firstName || interview.candidate?.lastName
                            ? `${interview.candidate?.firstName} ${interview.candidate?.lastName}`
                            : 'Unknown'}
                        </Link>
                      </Typography>
                    }
                    secondary={<>{renderListItemDate(interview.createdAt)}</>}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </>
      );
    } else {
      mainContent = <EmptyState message="No Incomplete Interviews" />;
    }
  }

  return {
    mainContent: (
      <DashboardBlock
        title={`Incomplete Interviews (${totalInterviewsWithoutNotesCount})`}
        icon={<EventNoteIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        gridSize={{ xs: 12, md: 12 }}
      >
        <Box id="interviews-without-notes" sx={getHighlightStyle('interviews-without-notes')}>
          {mainContent}
        </Box>
      </DashboardBlock>
    ),
    statCard: (
      <StatCard
        title="Incomplete Interviews"
        value={totalInterviewsWithoutNotesCount}
        onClick={() => scrollToSection('interviews-without-notes')}
      />
    ),
  };
};

export default useInterviewsWithoutNotes;
